import { FC, useState } from 'react';
import {
  Text,
  Heading,
  FormField,
  Input,
  Button,
  Form,
} from '@livechat/design-system-react-components';

import { StandardError } from 'types/error';
import { Layout } from 'components/layout/Layout';
import { UnexpectedError } from 'components/error-message/UnexpectedError';
import { useSharedStore } from 'stores/shared-store';
import { FormStates } from 'types/form';
import Location from 'services/Location';
import { validateEmail } from 'helpers/validators/email';
import { accountsClient } from 'services/connectivity/accounts/client';

import { ResetPasswordSent } from './ResetPasswordSent';

import * as styles from './styles';

export const ForgotPassword: FC = () => {
  const sharedStore = useSharedStore(['email']);

  const [email, setEmail] = useState<string>(
    (Location.getHashParam('email') && decodeURIComponent(Location.getHashParam('email'))) ||
      Location.getNthUrlParam(1) ||
      (sharedStore.get('email') as string) ||
      ''
  );
  const [formState, setFormState] = useState<string>(FormStates.Default);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [requestSuccess, setRequestSuccess] = useState<boolean | null>(null);
  const [requestError, setRequestError] = useState<StandardError | null>(null);

  const handleEmailInputChange = (e) => {
    if (!validateEmail(e.target.value)) {
      setValidationError(null);
      setFormState(FormStates.Default);
    }

    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailValidation = validateEmail(email);

    if (emailValidation) {
      setValidationError(emailValidation);
      return;
    }

    setFormState(FormStates.Requesting);

    const { error, response } = await accountsClient.resendPassword({ email });

    if (error) {
      setRequestError({
        responseCode: response.status,
        requestId: error.request_id,
        description: error.error_description,
      });

      return;
    }

    setRequestSuccess(true);
  };

  if (requestSuccess || (requestError && requestError.responseCode === 403)) {
    return <ResetPasswordSent email={email} />;
  }

  if (requestError && requestError.responseCode !== 403) {
    return (
      <UnexpectedError
        requestId={requestError.requestId ?? ''}
        description={requestError.description ?? ''}
      />
    );
  }

  return (
    <Layout showHeaderLogo showGoToSignIn>
      <div data-testid="forgot-password-form-wrapper" css={styles.container}>
        <Heading size="lg">Forgot password?</Heading>
        <Text>No worries! Just enter your email and we’ll send you login instructions.</Text>
        <div css={styles.formContainer}>
          <Form onSubmit={handleSubmit}>
            <FormField labelText="Business email" labelFor="email" error={validationError}>
              <Input
                type="email"
                name="email"
                placeholder="name@work-email.com"
                inputSize="large"
                value={email}
                onChange={handleEmailInputChange}
              />
            </FormField>

            <Button
              kind="primary"
              size="large"
              onClick={handleSubmit}
              loading={formState === FormStates.Requesting}
              css={styles.submitButton}
            >
              Send reset email
            </Button>
          </Form>
        </div>
      </div>
    </Layout>
  );
};
